import React, { useEffect, useState } from 'react';
import { Field, reduxForm, change } from 'redux-form';
import S from 'StyledRegisterWizardFormStep1.js';
import { translation } from 'helpers/utilsHelper.js';
import { isValueUnique } from 'authActions.js';
import useToggle from 'UseToggle.js';
import classNames from 'classnames';
import validation from 'sb-validate-js/validate.js';
import validators from 'validators.enum.js';
import store from 'store';

const validate = values => {
    const errors = validation(values, validators.REGISTER_STEP1);
    return errors;
};

const asyncValidate = async (values, dispatch, props, field) => {
    const previousErrors = props.asyncErrors;
    if (field) {
        const fieldName = field;
        const fieldValue = values[field];
        try {
            const result = await dispatch(isValueUnique(fieldName, fieldValue));
            if(previousErrors) {
                throw 'prevErrors';
            } else {
                return result;
            }
        } catch (err) {
            if(err == 'prevErrors') {
                throw previousErrors;
            } else {
                throw Object.assign({}, previousErrors, { [fieldName]: [translation("account_registerForm_" + err)]});
            }
        }
    }
};

const PasswordToggler = () => {
    const [passwordVisibility, togglePassword] = useToggle(false);

    useEffect(() => {
        const passwordFields = document.querySelectorAll('[name="password"]');
        if (passwordFields.length) {
            const passwordFieldsDOM = [...passwordFields];
            if (passwordVisibility) {
                passwordFieldsDOM.forEach(el => el.type = 'text');
            } else {
                passwordFieldsDOM.forEach(el => el.type = 'password');
            }
        }
    }, [passwordVisibility]);

    return (
        <S.PasswordToggler onClick={togglePassword}>
            <S.PasswordTogglerIcon isActive={passwordVisibility} className="fa fa-eye"></S.PasswordTogglerIcon>
        </S.PasswordToggler>
    );
};

const renderField = ({ input, label, type, disabled, meta: { touched, error, warning, valid, asyncValidating, submitFailed } }) => {

    const isDisabled = (typeof disabled != 'undefined') ? disabled : false;
    const inputClass = classNames({
        'async-validating': asyncValidating,
        'has-error': ((touched || submitFailed) && error),
        'is-valid': ((touched || submitFailed) && valid)
    });

    return (
        <S.Row>
          <S.InputWrapper>
            {input.name == 'password' && <PasswordToggler />}
            <S.Input {...input} type={type} required="required" className={inputClass} disabled={isDisabled} />
            <S.Label>{label}</S.Label>
            {(touched || submitFailed) && ((error && <S.Error className="text-ellipsis">{error[0]}</S.Error>) || (warning &&
                <S.Error>{warning}</S.Error>))}
          </S.InputWrapper>
        </S.Row>
    );
};

const renderCheckbox = ({showFullText = false,  input, label, required, meta: { touched, error, warning, submitFailed } }) => {

    let [fullText, setFullText] = useState(false);
 
    const toggleFullText = () => {
   
       if (label.length <= 100) {
           return;
       } else {
           setFullText(!fullText);
       }
    };
   
   const createLabelMarkup = () => {
       let more;
       let currLabel;
       if(label.length <= 100 || showFullText) {
           currLabel = label;
           more = '';
       } else if (!fullText && label.length > 100) {
           currLabel = label.slice(0, 100);
           more = `<span>(${translation('account_registerForm_showMore')})</span>`;
       } else {
           currLabel = label;
           more = `<span>(${translation('account_registerForm_showLess')})</span>`;
       }
       return { __html: `<span>${currLabel}</span>${more}` };
   };
  
    return (
        <S.Row>
            <S.CheckboxWrapper className="with-checkbox">
                <S.Input {...input} 
                         value={input.value} 
                         checked={input.value}
                         onChange={event => input.onChange(event.target.checked)} type="checkbox" required={required} 
                />
                <S.FakeChbox>
                    {input.value && <S.CheckMark></S.CheckMark>}
                </S.FakeChbox>

                <label 
                    dangerouslySetInnerHTML={createLabelMarkup()} 
                    onClick={toggleFullText}
                />

                {(touched || submitFailed) && ((error && <S.Error>{error[0]}</S.Error>) || (warning &&
                <S.Error>{warning}</S.Error>))}
            </S.CheckboxWrapper>
        </S.Row>   
    );
};

const normalizeMobile = value => value && value.replace(/[\s\-]/g,'');

const handleSelectAllFields = (event, newValue, prevValue) => {
    const { dispatch } = store;
    if(newValue && newValue != prevValue) {
        dispatch(change('registerWizardForm', 'marketingConsent', true));
        dispatch(change('registerWizardForm', 'rodoClause', true));
    } else {
        dispatch(change('registerWizardForm', 'marketingConsent', false));
        dispatch(change('registerWizardForm', 'rodoClause', false));
    }
}

const RegisterWizardFormStep1 = (props) => {

    const { handleSubmit, submitting, error } = props;

    return (
    <>
        <S.RegisterWizardFormStep1 className="register-step1" onSubmit={handleSubmit} autocomplete="off" noValidate>
           
                {error && <S.SubmissionError className="submission-error">{error}</S.SubmissionError>}

                <Field
                    name="email"
                    type="text"
                    component={renderField}
                    label={translation('account_registerForm_email')}
                />

                <Field
                    name="password"
                    type="password"
                    component={renderField}
                    label={translation('account_registerForm_password')}
                />
                <Field
                    name="mobile"
                    type="text"
                    component={renderField}
                    label={translation('account_registerForm_phoneNumber')}
                    normalize={normalizeMobile}
                />

                <Field
                    name="selectAllFields"
                    component={renderCheckbox}
                    label={translation('account_registerForm_marketing_selectAll')}
                    required={false}
                    onChange={handleSelectAllFields}
                />
                
                <Field
                    name="rodoClause"
                    component={renderCheckbox}
                    type="checkbox"
                    label={translation('account_registerForm_rodoClause')}
                    required={true}
                />

                <Field
                    name="marketingConsent"
                    component={renderCheckbox}
                    type="checkbox"
                    label={translation('account_registerForm_marketing_receiveEmail')}
                    required={false}
                    showFullText
                />

                <S.AdditionalInfo>{translation('account_registerForm_additionalInfo')}</S.AdditionalInfo>

        </S.RegisterWizardFormStep1>
        <S.SubmitButton onClick={handleSubmit} className="next">{translation('account_registerForm_next')}</S.SubmitButton>
    </>
    )
}

export default reduxForm({
    form: 'registerWizardForm',
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
    validate,
    asyncValidate,
    asyncBlurFields: ['email', 'mobile']
})(RegisterWizardFormStep1);